import { useMemo } from "react";

import { useRecommendedJobs, useUserAccount } from "../../../services/create-resume/queries";
import JobListingItem from "../../JobsPage/components/JobListingItem";
import PreferencesUpsellCard from "./PreferencesUpsellCard";

interface RecommendedJobsProps {
	setShowPreferencesModal: (show: boolean) => void;
	setSelectedJob: (job: unknown) => void;
	setShowJobDetailsModal: (show: boolean) => void;
}

function RecommendedJobs({ setShowPreferencesModal, setSelectedJob, setShowJobDetailsModal }: RecommendedJobsProps) {
	const { data: account, isLoading: isLoadingAccount } = useUserAccount();

	const { data: recommendedJobs, isLoading: isLoadingRecommendedJobs } = useRecommendedJobs(
		account?.jobPreferences.areasOfExpertise,
	);

	// Never list a job with a missing company — skip it entirely.
	const jobs = useMemo(() => (recommendedJobs ?? []).filter((job) => Boolean(job?.company)), [recommendedJobs]);

	if (isLoadingAccount || isLoadingRecommendedJobs) {
		return (
			<div className="animate-pulse">
				{[...new Array(5)].map((_, index) => (
					<div className="h-24 bg-gray-900 opacity-10 mb-3 rounded-md" key={index} />
				))}
			</div>
		);
	}

	const progress = account?.job_preference_progress_status;
	const completenessPct = progress?.completeness_pct ?? 0;

	// Nothing filled in at all — no expertise means no recommendations are possible, so the upsell
	// takes over the whole slot.
	if (completenessPct === 0) {
		return (
			<PreferencesUpsellCard
				variant="empty"
				completenessPct={completenessPct}
				onOpenPreferences={() => setShowPreferencesModal(true)}
			/>
		);
	}

	// The partially-complete nudge lives at the top of the profile page now, not above this list.
	return (
		<div className="divide-y divide-gray-200">
			{jobs.map((job, index) => (
				<JobListingItem
					key={index}
					job={{
						...job,
						logo: job.company.logo_url,
					}}
					onApply={() => {
						setSelectedJob(job);
						setShowJobDetailsModal(true);
					}}
					onClick={() => {
						setSelectedJob(job);
						setShowJobDetailsModal(true);
					}}
					showCompany={true}
					titleClassName="text-base"
					showMatchScore
				/>
			))}
		</div>
	);
}

export default RecommendedJobs;
